import { createFileRoute } from "@tanstack/react-router";
import { canonical } from "@/components/site/site-config";
import { Phone, Mail, MapPin, ShieldCheck, Clock } from "lucide-react";
import { PageHero } from "@/components/site/PageShell";
import { ContactForm } from "@/components/site/ContactForm";
import { BUSINESS } from "@/components/site/constants";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Request a Free Roof Inspection | Citrus County, FL" },
      { name: "description", content: "Contact Destination Roofing for roof replacement, repair, metal, shingle and flat roofing, gutters, soffit and wind-mitigation services in Citrus County, FL." },
      { property: "og:title", content: "Request a Free Roof Inspection | Citrus County, FL" },
      { property: "og:description", content: `Free Roof Inspection & Estimate. Serving Citrus County, FL. ${BUSINESS.license}.` },
      { property: "og:url", content: canonical("/contact") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/contact") }],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Contact Us"
        title="Request a Free Roof Inspection in Citrus County, FL"
        subtitle={`Tell us about your roof — we'll get back to you quickly. Prefer to talk now? Call ${BUSINESS.phone}.`}
      />
      <section className="mx-auto max-w-7xl px-4 py-14 grid gap-10 lg:grid-cols-5">
        <div className="lg:col-span-2 space-y-6">
          <InfoCard icon={<Clock className="size-5" />} title="Business hours">
            <ul className="text-sm space-y-0.5">
              <li>Monday – Saturday: 6:30 AM – 5:00 PM</li>
              <li>Sunday: Closed</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">{BUSINESS.emergencyLine}</p>
          </InfoCard>
          <InfoCard icon={<Phone className="size-5" />} title="Call or text" >
            <a href={BUSINESS.phoneHref} className="text-2xl font-display font-bold text-brand hover:text-cta">{BUSINESS.phone}</a>
            <div className="text-sm text-muted-foreground mt-1">Talk to a real local person — not a call center.</div>
          </InfoCard>
          <InfoCard icon={<Mail className="size-5" />} title="Email us">
            <a href={BUSINESS.emailHref} className="font-display font-bold text-brand hover:text-cta break-all">{BUSINESS.email}</a>
            <div className="text-sm text-muted-foreground mt-1">We reply quickly during business hours.</div>
          </InfoCard>

          <InfoCard icon={<MapPin className="size-5" />} title="Service area">
            <div>{BUSINESS.serviceArea}</div>
            <div className="text-sm text-muted-foreground mt-1">Beverly Hills · Inverness · Crystal River · Homosassa · Lecanto · Spring Hill</div>
          </InfoCard>
          <InfoCard icon={<ShieldCheck className="size-5" />} title="Licensed & insured">
            <div>{BUSINESS.license}</div>
            <div className="text-sm text-muted-foreground mt-1">Family-owned Florida roofing contractor.</div>
          </InfoCard>
          <InfoCard icon={<Clock className="size-5" />} title="Emergency roof repair">
            <div>Active roof leak or storm damage?</div>
            <a href={BUSINESS.phoneHref} className="btn-cta mt-3"><Phone className="size-4" /> Call Now</a>
          </InfoCard>
        </div>
        <div className="lg:col-span-3">
          <ContactForm formName="contact_page" />
        </div>
      </section>
    </>
  );
}

function InfoCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
      <div className="flex items-center gap-3 mb-2">
        <div className="size-10 rounded-lg bg-brand/10 text-brand grid place-items-center">{icon}</div>
        <div className="font-display font-bold text-lg">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}
